import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { uid } from '../../shared/utils/helpers';
import { ratingLabel } from '../../shared/logic/scoring';
import type { Player, SkillLevel } from '../../shared/types';
import UpgradeBanner from '../components/UpgradeBanner';

function isPlanLimit(msg: string) { return msg.startsWith('Free plan'); }

export default function Players() {
  const { players, league, session, service } = useApp();
  const [name, setName]   = useState('');
  const [dupr, setDupr]   = useState('');
  const [skill, setSkill] = useState<SkillLevel | ''>('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Session status helpers ────────────────────────────────
  const playingIds  = new Set(session?.courts.flatMap(c => [...c.teamA, ...c.teamB]) ?? []);
  const benchedIds  = new Set(session?.benched ?? []);
  const checkedInIds = new Set(session?.checkedIn ?? []);

  type Status = 'court' | 'waiting' | 'benched' | 'out';
  function playerStatus(id: string): Status | null {
    if (!session) return null;
    if (benchedIds.has(id))   return 'benched';
    if (playingIds.has(id))   return 'court';
    if (checkedInIds.has(id)) return 'waiting';
    return 'out';
  }

  async function benchPlayer(id: string) {
    if (!session) return;
    const current = session.benched ?? [];
    await service.setSession({
      ...session,
      benched: [...current, id],
      waiting: session.waiting.filter(w => w !== id),
    });
  }

  async function returnPlayer(id: string) {
    if (!session) return;
    const current = session.benched ?? [];
    await service.setSession({
      ...session,
      benched: current.filter(b => b !== id),
    });
  }

  // ── League actions ────────────────────────────────────────
  async function addPlayer() {
    const trimmed = name.trim();
    if (!trimmed) { setError('Please enter a name.'); return; }
    if (players.some(p => p.name.toLowerCase() === trimmed.toLowerCase())) {
      setError(`"${trimmed}" is already registered.`);
      return;
    }
    if (league && players.length >= league.maxPlayers) {
      setError('League is full. Increase the player limit in Settings or upgrade to Pro.');
      return;
    }
    const duprVal = parseFloat(dupr);
    const player: Player = {
      id: uid(),
      name: trimmed,
      dupr: isNaN(duprVal) ? null : duprVal,
      skill: skill || null,
      joined: new Date().toISOString(),
    };
    setSaving(true);
    try {
      await service.addPlayer(player);
      setName(''); setDupr(''); setSkill(''); setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add player. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function removePlayer(id: string) {
    if (!confirm('Remove this player from the league?')) return;
    await service.removePlayer(id);
  }

  const spotsLeft = league ? league.maxPlayers - players.length : null;

  const statusBadge: Record<NonNullable<Status>, { label: string; cls: string }> = {
    court:   { label: 'On Court',   cls: 'badge-green'  },
    waiting: { label: 'Waiting',    cls: 'badge-yellow' },
    benched: { label: 'Benched',    cls: 'badge-gray'   },
    out:     { label: 'Not in session', cls: 'badge-gray' },
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>

      {/* Registration bar */}
      {league && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 700 }}>{players.length} / {league.maxPlayers} players</span>
            <span className={`badge ${spotsLeft === 0 ? 'badge-red' : spotsLeft! <= 5 ? 'badge-yellow' : 'badge-green'}`}>
              {spotsLeft === 0 ? 'Full' : `${spotsLeft} spots left`}
            </span>
          </div>
          <div style={{ background: 'var(--gray-200)', borderRadius: 99, height: 8, overflow: 'hidden' }}>
            <div style={{
              background: spotsLeft === 0 ? 'var(--red)' : spotsLeft! <= 5 ? 'var(--yellow)' : 'var(--green)',
              height: '100%',
              width: `${(players.length / league.maxPlayers) * 100}%`,
              transition: 'width .4s',
              borderRadius: 99,
            }} />
          </div>
        </div>
      )}

      {/* Add player form */}
      <div className="card">
        <div className="card-title">➕ Add Player</div>
        {error && isPlanLimit(error)
          ? <UpgradeBanner message={error} />
          : error && <div className="alert alert-warn">{error}</div>
        }
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ flex: 2, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label>Name *</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addPlayer()}
              placeholder="Player name"
            />
          </div>
          <div style={{ flex: 1, minWidth: 100, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label>DUPR Rating</label>
            <input
              type="number" step="0.01" min="2" max="6"
              value={dupr}
              onChange={e => setDupr(e.target.value)}
              placeholder="e.g. 3.75"
            />
          </div>
          <div style={{ flex: 1, minWidth: 120, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label>Skill Level</label>
            <select value={skill} onChange={e => setSkill(e.target.value as SkillLevel | '')}>
              <option value="">Select…</option>
              {(['Beginner','Intermediate','Advanced','Elite'] as SkillLevel[]).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <button className="btn btn-primary" onClick={addPlayer} disabled={saving}>
          {saving ? 'Adding…' : '+ Add Player'}
        </button>
      </div>

      {/* Player list */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Roster ({players.length})</div>
          {session && (
            <span style={{ fontSize: '.78rem', color: 'var(--gray-500)' }}>
              Session active — tap Bench to remove a player from future rounds
            </span>
          )}
        </div>
        {!players.length ? (
          <div className="empty">
            <div className="empty-icon">👤</div>
            No players yet. Add the first one above.
          </div>
        ) : (
          <div className="player-grid">
            {players.map(p => {
              const status = playerStatus(p.id);
              const sb = status ? statusBadge[status] : null;
              const isBenched  = status === 'benched';
              const isInSession = status === 'court' || status === 'waiting' || status === 'benched';
              return (
                <div
                  key={p.id}
                  className="player-card"
                  style={{ opacity: isBenched ? 0.55 : 1 }}
                >
                  <div className="pname" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {p.name}
                    {sb && <span className={`badge ${sb.cls}`} style={{ fontSize: '.68rem' }}>{sb.label}</span>}
                  </div>
                  <div className="meta">
                    <span className="badge badge-blue">{ratingLabel(p)}</span>
                    {session && isInSession && !isBenched && (
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ marginLeft: 'auto', padding: '2px 10px', fontSize: '.72rem' }}
                        onClick={() => benchPlayer(p.id)}
                      >
                        🚫 Bench
                      </button>
                    )}
                    {session && isBenched && (
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ marginLeft: 'auto', padding: '2px 10px', fontSize: '.72rem' }}
                        onClick={() => returnPlayer(p.id)}
                      >
                        ↩ Return
                      </button>
                    )}
                    {!session && (
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: '.72rem' }}
                        onClick={() => removePlayer(p.id)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
